import { Component, OnInit, ViewChildren, QueryList, ElementRef, OnDestroy } from '@angular/core';
import { trigger, style, animate, transition, query, animateChild, keyframes } from '@angular/animations';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { GCValidators } from '../../shared/validators/gc-validator';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../shared/services/auth.service';
import { SnackbarService } from '../../theme/components/snackbar/snackbar.service';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
  animations: [
    trigger('stepChangeParent', [transition(':enter, :leave', [query('@*', animateChild())])]),
    trigger('stepChange', [
      transition('* => first', [
        style({ opacity: '0', transform: 'scale(.5)' }),
        animate(
          '.3s .4s ease-in',
          keyframes([
            style({ opacity: '0', transform: 'scale(.5)', offset: 0 }),
            style({ opacity: '1', transform: 'scale(1)', offset: 1 }),
          ]),
        ),
      ]),
      transition(':enter', [
        style({ opacity: '0', transform: 'translateX(100%)' }),
        animate(
          '.7s .4s ease-in-out',
          keyframes([
            style({ opacity: '0', transform: 'translateX(100%)', offset: 0 }),
            style({ opacity: '1', transform: 'translateX(0)', offset: 1 }),
          ]),
        ),
      ]),
      transition(':leave', [
        style({ opacity: '1', transform: 'translateX(0)', position: 'absolute', left: 0, right: 0 }),
        animate(
          '.7s ease-in-out',
          keyframes([
            style({ opacity: '1', transform: 'translateX(0)', offset: 0 }),
            style({ opacity: '0', transform: 'translateX(-100%)', offset: 1 }),
          ]),
        ),
      ]),
    ]),
  ],
})
export class SignUpComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  public currentStep: number = 0;
  private activateToken: string = '';
  private boToken: string = '';
  private registrationEmail: string = '';

  private company: any = {};
  private languagePreference: string = '';

  public signupError = '';

  registerForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.minLength(2), GCValidators.validateEmail()]),
    password: new FormControl('', [Validators.required, GCValidators.validatePassword()]),
    confirmPassword: new FormControl('', [Validators.required, GCValidators.validatePasswordComparison()]),
  });

  profileForm = new FormGroup({
    branch: new FormControl('', [Validators.required]),
    title: new FormControl('none', [Validators.required]),
    prename: new FormControl('', [Validators.required, Validators.minLength(2)]),
    surname: new FormControl('', [Validators.required, Validators.minLength(2)]),

    acceptToS: new FormControl('', Validators.requiredTrue),
    // acceptNewsletter: new FormControl('', Validators.requiredTrue),
  });

  @ViewChildren('step') steps: QueryList<ElementRef>;

  public selectTeamAfterRegistration: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private translate: TranslateService,
    private authService: AuthService,
    private router: Router,
    private snackBar: SnackbarService,
  ) {}

  public ngOnInit() {
    this.route.params.takeUntil(this.ngUnsubscribe).subscribe((values) => {
      this.activateRoute(values);
    });

    this.route.queryParams.takeUntil(this.ngUnsubscribe).subscribe((params) => {
      if (params.email && this.currentStep === 1) {
        this.registerForm.controls.email.patchValue(params.email);
      }
      if (params.selectTeam) {
        this.selectTeamAfterRegistration = true;
      }
    });
  }

  private activateRoute(params) {
    this.activateToken = params.token || '';
    this.boToken = params.botoken || '';

    // step 2->3 route
    if (params.data && params.data === 'verify') {
      // check if token is valid and get company data
      this.authService.getCompanyByVerificationToken(this.activateToken).then((data: any) => {
        this.currentStep = 3;
        this.company = data.company;
        this.translate.setDefaultLang(data.language);
        this.languagePreference = data.language;
        if (data.boID) {
          this.profileForm.controls.branch.setValue(data.boID);
          this.profileForm.controls.branch.disable();
        } else {
          if (!this.company.branchoffices || this.company.branchoffices.length === 0) {
            this.profileForm.controls.branch.setValidators(undefined);
          } else if (this.company.branchoffices.length === 1) {
            this.profileForm.controls.branch.setValue(this.company.branchoffices[0]._id);
          }
        }
      });
    } else if (params.data) {
      // step 1 - This is language info
      this.translate.setDefaultLang(params.data);
      this.languagePreference = params.data;

      this.currentStep = 1;
    }
  }

  submitRegisterForm(e) {
    e.preventDefault();

    if (this.registerForm.valid) {
      this.registrationEmail = this.registerForm.value.email;
      this.authService
        .registerUser(
          this.registerForm.value.email,
          this.registerForm.value.password,
          this.languagePreference,
          this.activateToken,
          this.boToken,
          this.selectTeamAfterRegistration,
        )
        .then((result) => {
          this.currentStep = 2;
          this.translate.use(this.translate.getDefaultLang());
        })
        .catch((response) => {
          const error = response.body.error;

          if (error === 'Account is already active.') {
            this.signupError = 'accountActive';
          }

          if (error === 'E-Mail is already taken.') {
            this.signupError = 'emailTaken';
            this.currentStep = 2;
            this.translate.use(this.translate.getDefaultLang());
          }

          if (error === 'Invalid token.') {
            this.signupError = 'invalidToken';
          }

          if (error === 'Specified e-mail domain is not allowed for this company.') {
            this.signupError = 'invalidDomain';
          }

          if (error === 'Direct registration to this company is not allowed.') {
            this.signupError = 'companyRegistrationBlocked';
          }
        });
    }
  }

  requestNewMail() {
    this.authService.resendActivationEmail(this.registrationEmail).then((result) => {});
  }

  submitProfileForm() {
    if (this.profileForm.valid) {
      this.authService
        .activateAccount(
          this.profileForm.value.branch,
          this.profileForm.value.title === 'male' ? true : this.profileForm.value.title === 'female' ? false : null,
          this.profileForm.value.prename,
          this.profileForm.value.surname,
          this.activateToken,
        )
        .then((result) => {
          if (result.challengeID && (result.hasInvitation || this.selectTeamAfterRegistration)) {
            // If challengeID is in result, that means we should redirect to
            this.router.navigate(['challenge', result.challengeID, 'team']).then();
          } else {
            this.router.navigate(['/dashboard']);
          }
        });
    }
  }

  /**
   * On destroy cancel active subscriptions
   */
  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src1521087412/src/client/web-client/src/app/pages/sign-up/sign-up.component.ts